import React, { useState } from 'react';
import { ArrowRight, Shield, Users, BookOpen, Award, CheckCircle, GraduationCap, Target } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ConsultationModal from '../../components/ConsultationModal';
import DemoModal from '../../components/DemoModal';

const SecurityTrainingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const trainingPrograms = [
    {
      icon: Users,
      title: "Security Awareness Training",
      description: "Comprehensive security awareness programs to educate employees about cybersecurity threats and best practices.",
      features: ["Phishing simulation", "Social engineering awareness", "Password security", "Data protection"]
    },
    {
      icon: Target,
      title: "Technical Security Training",
      description: "Advanced technical training for IT professionals and security teams on latest security technologies.",
      features: ["Penetration testing", "Incident response", "Security tools", "Threat hunting"]
    },
    {
      icon: Award,
      title: "Certification Preparation",
      description: "Preparation courses for industry-leading cybersecurity certifications and professional development.",
      features: ["CISSP preparation", "CEH training", "CISM certification", "Security+ bootcamp"]
    },
    {
      icon: BookOpen,
      title: "Custom Training Programs",
      description: "Tailored training programs designed specifically for your organization's security needs and requirements.",
      features: ["Industry-specific content", "Role-based training", "Compliance training", "Executive briefings"]
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
    }`}>
      <Header 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      <ConsultationModal 
        isOpen={isConsultationModalOpen} 
        onClose={() => setIsConsultationModalOpen(false)} 
      />
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Hero Section */}
      <section className={`relative py-20 overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-slate-100' 
          : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 text-slate-900'
      }`}>
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Security Training"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-slate-950/90 to-indigo-950/90' 
              : 'bg-gradient-to-r from-indigo-50/90 to-pink-50/90'
          }`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <GraduationCap className={`h-16 w-16 mx-auto mb-6 ${
              isDarkMode ? 'text-indigo-400' : 'text-indigo-600'
            }`} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Security <span className={isDarkMode ? "text-indigo-400" : "text-indigo-600"}>Training</span>
            </h1>
            <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}>
              Empower your team with comprehensive cybersecurity training programs 
              that build security awareness and technical expertise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                  isDarkMode 
                    ? "bg-indigo-600 hover:bg-indigo-700 text-white hover:shadow-indigo-500/25" 
                    : "bg-indigo-600 hover:bg-indigo-700 text-white hover:shadow-indigo-500/25"
                }`}
              >
                <Shield className="mr-2 h-5 w-5" />
                Start Training Program
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 border-2 ${
                  isDarkMode 
                    ? "border-indigo-400 text-indigo-400 hover:bg-indigo-400 hover:text-slate-900" 
                    : "border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white"
                }`}
              >
                View Training Catalog
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Training Programs Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Comprehensive Training Programs
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              Build security expertise across your organization with our training programs
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {trainingPrograms.map((program, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
                isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
              }`}>
                <program.icon className={`h-12 w-12 mb-4 ${
                  isDarkMode ? 'text-indigo-400' : 'text-indigo-600'
                }`} />
                <h3 className={`text-xl font-semibold mb-3 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{program.title}</h3>
                <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {program.description}
                </p>
                <ul className="space-y-2">
                  {program.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className={`h-4 w-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer 
        onConsultationClick={() => setIsConsultationModalOpen(true)}
        onDemoClick={() => setIsDemoModalOpen(true)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default SecurityTrainingPage;